/*
 * lib/import.js
 *
 * Importeer een eerder geëxporteerd Florijn-backup-bestand (JSON) en
 * herstel het in Supabase. Aanroepvolgorde:
 *
 *   1. parseImportFile(text)
 *        → throwt bij ongeldig formaat/versie. Geeft 'parsed' terug.
 *
 *   2. summarizeImport(parsed)
 *        → returns een leesbare samenvatting voor de confirm-dialog.
 *
 *   3. applyImport(parsed)
 *        → wipet alle bestaande DB-rijen voor de ingelogde user (+ samen,
 *          als die in de payload zit), en upsert alles uit het bestand.
 *
 * Na success: de caller hoort `location.reload()` te triggeren zodat
 * hydrate het opnieuw laadt vanaf de schone DB.
 *
 * Format-verwacht: identiek aan wat lib/export.js wegschrijft.
 */

import { state } from '../state.js';
import {
  upsertExpense, expenseStateToRow,
  upsertIncome, incomeStateToRow,
  upsertWeeklyBudget,
  upsertCategory,
  upsertSavingsGoal, savingsGoalStateToRow,
  upsertExtraEntry, extraEntryStateToRow,
  patchProfile, profilePatchStateToDb,
  deleteAllPersonalData, deleteAllHouseholdData
} from './supabase.js';

const VERSION = 1;

export function parseImportFile(text) {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('Bestand is geen geldige JSON.');
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Bestand heeft niet de verwachte structuur.');
  }
  if (parsed.florijn_export_version !== VERSION) {
    throw new Error(
      `Bestand-versie ${parsed.florijn_export_version || '?'} wordt niet ondersteund (verwacht v${VERSION}).`
    );
  }
  if (!parsed.data || !parsed.data.me) {
    throw new Error('Bestand bevat geen persoonlijke data (data.me ontbreekt).');
  }
  return parsed;
}

export function summarizeImport(parsed) {
  const me = parsed.data.me || {};
  const cats = parsed.data.cats || [];
  const samen = parsed.data.samen;
  const date = parsed.exported_at ? parsed.exported_at.slice(0, 16).replace('T', ' ') : '?';
  const histCount = Object.values(me.hist || {})
    .reduce((s, h) => s + (h.ex?.length || 0) + (h.in?.length || 0), 0);
  return [
    `Geëxporteerd: ${date}`,
    `Gebruiker:    ${parsed.username || parsed.person || '?'}`,
    `Categorieën:  ${cats.length}`,
    `Vast:         ${me.vast?.length || 0}`,
    `Variabel:     ${me.var?.length || 0}`,
    `Inkomsten:    ${me.inkomsten?.length || 0}`,
    `Dromen:       ${me.dromen?.length || 0}`,
    `Weekbudgets:  ${Object.keys(me.weekBudgets || {}).length}`,
    `Hist-entries: ${histCount}`,
    samen ? `Samen: ${samen.vast?.length || 0} vast, ${samen.var?.length || 0} var` : 'Geen samen-data in bestand'
  ].join('\n');
}

/** Hervat. Wipet alle DB-rijen voor user (+ samen indien relevant) en
 *  schrijft alles uit het bestand opnieuw. Throwt bij eerste fout — de
 *  caller moet de gebruiker waarschuwen dat de toestand inconsistent
 *  kan zijn. */
export async function applyImport(parsed) {
  if (!state.userId) throw new Error('Niet ingelogd — kan niet importeren.');

  const me      = parsed.data.me || {};
  const cats    = parsed.data.cats || [];
  const settings = parsed.data.settings || {};
  const samen   = parsed.data.samen;
  const userId  = state.userId;
  const householdId = state.householdId;

  // ───── 1. Schoonvegen ─────
  await deleteAllPersonalData(userId);
  if (householdId && samen) {
    await deleteAllHouseholdData(householdId);
  }

  // ───── 2. Categorieën ─────
  for (const cat of cats) {
    await upsertCategory(userId, cat);
  }

  // ───── 3. Persoonlijke vast/var ─────
  const vastArr = me.vast || [];
  for (let i = 0; i < vastArr.length; i++) {
    const item = ensureId(vastArr[i]);
    await upsertExpense(expenseStateToRow(item, { userId, scope: 'vast', sortOrder: i }));
  }
  const varArr = me.var || [];
  for (let i = 0; i < varArr.length; i++) {
    const item = ensureId(varArr[i]);
    await upsertExpense(expenseStateToRow(item, { userId, scope: 'var', sortOrder: i }));
  }

  // ───── 4. Inkomsten ─────
  const incArr = me.inkomsten || [];
  for (let i = 0; i < incArr.length; i++) {
    const item = ensureId(incArr[i]);
    await upsertIncome(incomeStateToRow(item, { userId, sortOrder: i }));
  }

  // ───── 5. Savings goals (dromen) ─────
  const dromenArr = me.dromen || [];
  for (let i = 0; i < dromenArr.length; i++) {
    const item = ensureId(dromenArr[i]);
    await upsertSavingsGoal(savingsGoalStateToRow(item, { userId, sortOrder: i }));
  }

  // ───── 6. Weekbudgets ─────
  for (const [catId, amt] of Object.entries(me.weekBudgets || {})) {
    if (Number(amt) > 0) {
      await upsertWeeklyBudget(userId, catId, Number(amt));
    }
  }

  // ───── 7. hist.ex + hist.in voor persoonlijke ─────
  const meExpenseId = (vrKey) => resolveVrKeyId(vrKey, me);
  for (const [ck, h] of Object.entries(me.hist || {})) {
    for (const e of (h.ex || [])) {
      const ee = ensureId(e);
      await upsertExtraEntry(extraEntryStateToRow(ee, {
        userId, direction: 'uit', cycleKey: ck,
        relatedExpenseId: meExpenseId(ee.vrKey)
      }));
    }
    for (const e of (h.in || [])) {
      const ee = ensureId(e);
      await upsertExtraEntry(extraEntryStateToRow(ee, {
        userId, direction: 'in', cycleKey: ck
      }));
    }
  }

  // ───── 8. Profile-patch (settings + JSONB-velden) ─────
  const profilePatch = profilePatchStateToDb({
    shareWithMate:      me.shareWithMate,
    gemAuto:            me.gemAuto,
    spaarAuto:          me.spaarAuto,
    gemHandled:         me.gemHandled,
    spaarHandled:       me.spaarHandled,
    surplusHandled:     me.surplusHandled,
    cycleReviews:       me.cycleReviews,
    huidigSaldo:        me.huidigSaldo,
    huidigSaldoUpdated: me.huidigSaldoUpdated,
    pensioenActief:     me.pensioenActief,
    pensioenGrens:      me.pensioenGrens,
    belastingPct:       me.belastingPct,
    schulden:           me.schulden,
    externeSchulden:    me.externeSchulden,
    appStartDate:       settings.appStartDate,
    mode:               settings.mode,
    theme:              settings.theme,
    remainingView:      settings.remainingView,
    loonHandled:        me.loonHandled
  });
  if (Object.keys(profilePatch).length > 0) {
    await patchProfile(userId, profilePatch);
  }

  // ───── 9. Samen-data (optioneel, alleen als user in household zit) ─────
  if (householdId && samen) {
    const sVast = samen.vast || [];
    for (let i = 0; i < sVast.length; i++) {
      const item = ensureId(sVast[i]);
      await upsertExpense(expenseStateToRow(item, { householdId, scope: 'vast', sortOrder: i }));
    }
    const sVar = samen.var || [];
    for (let i = 0; i < sVar.length; i++) {
      const item = ensureId(sVar[i]);
      await upsertExpense(expenseStateToRow(item, { householdId, scope: 'var', sortOrder: i }));
    }
    const samenExpenseId = (vrKey) => resolveVrKeyId(vrKey, samen);
    for (const [ck, h] of Object.entries(samen.hist || {})) {
      for (const e of (h.ex || [])) {
        const ee = ensureId(e);
        await upsertExtraEntry(extraEntryStateToRow(ee, {
          householdId, direction: 'uit', cycleKey: ck,
          relatedExpenseId: samenExpenseId(ee.vrKey)
        }));
      }
      for (const e of (h.in || [])) {
        const ee = ensureId(e);
        await upsertExtraEntry(extraEntryStateToRow(ee, {
          householdId, direction: 'in', cycleKey: ck
        }));
      }
    }
  }
}

function ensureId(obj) {
  if (!obj.__id && typeof crypto?.randomUUID === 'function') {
    obj.__id = crypto.randomUUID();
  }
  return obj;
}

/** vrKey ('vast:N' / 'var:N') → __id van het item in scope.vast/var.
 *  Voor de import-flow: expenses zijn al voorzien van een __id via ensureId
 *  vóór we hier komen, dus de lookup werkt in-memory. */
function resolveVrKeyId(vrKey, scope) {
  if (!vrKey || !scope) return null;
  const [vType, vIdxStr] = String(vrKey).split(':');
  const vIdx = parseInt(vIdxStr, 10);
  return scope?.[vType]?.[vIdx]?.__id || null;
}
